import React, { useRef, useEffect, useState, useCallback } from 'react';

interface Potato {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  directionChangeCounter: number;
}

const PotatoGame: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [potatoes, setPotatoes] = useState<Potato[]>([]);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const cursorPosRef = useRef<{ x: number; y: number } | null>(null);
  const potatoIdRef = useRef(0);
  const animationFrameRef = useRef<number>(0);

  // Initialize potatoes
  useEffect(() => {
    const newPotatoes: Potato[] = [];
    for (let i = 0; i < 1; i++) {
      const container = containerRef.current;
      if (!container) return;

      newPotatoes.push({
        id: potatoIdRef.current++,
        x: Math.random() * (container.clientWidth - 50),
        y: Math.random() * (container.clientHeight - 50),
        vx: (Math.random() - 0.5) * 2.5,
        vy: (Math.random() - 0.5) * 2.5,
        size: 40 + Math.random() * 20,
        directionChangeCounter: 0,
      });
    }
    setPotatoes(newPotatoes);
  }, []);

  // Animation loop
  useEffect(() => {
    const animate = () => {
      const container = containerRef.current;
      if (!container) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      setPotatoes((prev) => {
        const updated = prev.map((p) => {
          let { x, y, vx, vy } = p;

          // Cursor repulsion - push potatoes away from cursor
          if (cursorPosRef.current) {
            const dx = p.x + p.size / 2 - cursorPosRef.current.x;
            const dy = p.y + p.size / 2 - cursorPosRef.current.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const repulsionRadius = 400; // How far the cursor affects potatoes

            if (dist < repulsionRadius && dist > 0) {
              // Push potato away from cursor
              const repulsionStrength = (repulsionRadius - dist) / repulsionRadius * 3;
              const angle = Math.atan2(dy, dx);
              vx += Math.cos(angle) * repulsionStrength;
              vy += Math.sin(angle) * repulsionStrength;
            }
          }

          // Update position
          x += vx;
          y += vy;

          // Bounce off walls - maintain speed in reflected direction
          if (x <= 0 || x + p.size >= container.clientWidth) {
            vx *= -0.95;
            x = Math.max(0, Math.min(x, container.clientWidth - p.size));
          }
          if (y <= 0 || y + p.size >= container.clientHeight) {
            vy *= -0.95;
            y = Math.max(0, Math.min(y, container.clientHeight - p.size));
          }

          // Maintain constant speed
          const speed = Math.sqrt(vx * vx + vy * vy);
          if (speed > 0.1) {
            // Normalize and keep speed at ~1.3 px/frame
            vx = (vx / speed) * 1.3;
            vy = (vy / speed) * 1.3;
          } else if (speed < 0.05) {
            // Give stationary potatoes a nudge
            const angle = Math.random() * Math.PI * 2;
            vx = Math.cos(angle) * 1.5;
            vy = Math.sin(angle) * 1.5;
          }

          return { ...p, x, y, vx, vy, directionChangeCounter: 0 };
        });

        // Collision detection
        for (let i = 0; i < updated.length; i++) {
          for (let j = i + 1; j < updated.length; j++) {
            const p1 = updated[i];
            const p2 = updated[j];
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const minDist = (p1.size + p2.size) / 2;

            if (dist < minDist) {
              // Elastic collision response
              const angle = Math.atan2(dy, dx);
              const sin = Math.sin(angle);
              const cos = Math.cos(angle);

              // Swap velocities along collision normal
              const vx1 = p1.vx * cos + p1.vy * sin;
              const vy1 = p1.vy * cos - p1.vx * sin;
              const vx2 = p2.vx * cos + p2.vy * sin;
              const vy2 = p2.vy * cos - p2.vx * sin;

              p1.vx = vx2 * cos - vy1 * sin;
              p1.vy = vy1 * cos + vx2 * sin;
              p2.vx = vx1 * cos - vy2 * sin;
              p2.vy = vy2 * cos + vx1 * sin;

              // Maintain speed after collision
              const speed = 1.5;
              const speed1 = Math.sqrt(p1.vx * p1.vx + p1.vy * p1.vy);
              const speed2 = Math.sqrt(p2.vx * p2.vx + p2.vy * p2.vy);
              if (speed1 > 0.1) {
                p1.vx = (p1.vx / speed1) * speed;
                p1.vy = (p1.vy / speed1) * speed;
              }
              if (speed2 > 0.1) {
                p2.vx = (p2.vx / speed2) * speed;
                p2.vy = (p2.vy / speed2) * speed;
              }

              // Reset direction change counter on collision so they pick new direction
              p1.directionChangeCounter = 0;
              p2.directionChangeCounter = 0;

              // Separate potatoes
              const overlap = minDist - dist;
              const moveX = (overlap / 2) * cos;
              const moveY = (overlap / 2) * sin;
              p1.x -= moveX;
              p1.y -= moveY;
              p2.x += moveX;
              p2.y += moveY;
            }
          }
        }

        return updated;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const handleMouseDown = (id: number, e: React.MouseEvent) => {
    e.preventDefault();
    setDraggingId(id);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStart) return;

    const container = containerRef.current;
    if (!container) return;

    setPotatoes((prev) => {
      const currentDraggingId = prev.find(p => p.id === (prev as any).draggingId)?.id;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      return prev.map((p) => {
        // Get the dragging ID from component state
        return p.id === draggingId ? { ...p, x: x - p.size / 2, y: y - p.size / 2 } : p;
      });
    });
  }, [dragStart, draggingId]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (draggingId === null || !dragStart) {
      setDraggingId(null);
      setDragStart(null);
      return;
    }

    const container = containerRef.current;
    if (!container) {
      setDraggingId(null);
      setDragStart(null);
      return;
    }

    const rect = container.getBoundingClientRect();
    const endX = e.clientX - rect.left;
    const endY = e.clientY - rect.top;

    const vx = (endX - dragStart.x) * 0.3;
    const vy = (endY - dragStart.y) * 0.3;

    setPotatoes((prev) =>
      prev.map((p) =>
        p.id === draggingId ? { ...p, vx, vy, directionChangeCounter: 0 } : p
      )
    );

    setDraggingId(null);
    setDragStart(null);
  }, [draggingId, dragStart]);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (draggingId !== null) {
      window.addEventListener('mousemove', handleMouseMove as any);
      window.addEventListener('mouseup', handleMouseUp as any);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove as any);
        window.removeEventListener('mouseup', handleMouseUp as any);
      };
    }
  }, [draggingId, dragStart]);

  // Track cursor position for potato repulsion
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Only update if cursor is over the container
      if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
        cursorPosRef.current = { x, y };
      } else {
        cursorPosRef.current = null;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {potatoes.map((p) => (
        <img
          key={p.id}
          src="/images/potato.png"
          alt="Potato"
          onMouseDown={(e) => handleMouseDown(p.id, e)}
          style={{
            position: 'absolute',
            left: `${p.x}px`,
            top: `${p.y}px`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            cursor: draggingId === p.id ? 'grabbing' : 'grab',
            userSelect: 'none',
            pointerEvents: 'auto',
            transition: draggingId === p.id ? 'none' : undefined,
          }}
          draggable={false}
        />
      ))}
    </div>
  );
};

export default PotatoGame;
